import { getItemUniqueKey, getPackageId } from 'checkout/lib/utils/accommodation/cart'
import { getOptInBundleInclusionsFromRate } from 'checkout/lib/utils/accommodation/bundles'
import { getPropertyRateUpgradeSourceItemTotals, isPropertyRateLoyaltyPackageUpgradeItem } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import type { IntlShape } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { applyChannelMarkupAndRoundUpToTen } from 'lib/channelMarkup/channelMarkup'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails, type ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'

function occupancyFromSession(session: App.AccommodationEnquirySession): App.Occupants {
  const { occupancy } = session.data
  return {
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAges,
  }
}

/**
 * Société earn class for the rate the enquiry matched. Mirrors
 * `generateLoyaltyPointsCalculatorPropertyOptions`, which the property page earns with.
 */
function getAccommodationV2LoyaltyProductType(
  rate: App.RawPropertyRate | undefined,
): 'hotel_lpc' | 'hotel_lpp_eps' | undefined {
  switch (rate?.service.type) {
    case 'lpc':
      return 'hotel_lpc'
    case 'bedbank':
      return 'hotel_lpp_eps'
    default:
      return undefined
  }
}

function emptyTotals(): App.Checkout.ItemViewTotals {
  return {
    price: 0,
    memberPrice: 0,
    value: 0,
    memberValue: 0,
    surcharge: 0,
    extraGuestSurcharge: 0,
    taxesAndFees: 0,
  }
}

/**
 * Cart-review totals from the shopped snapshot on the item.
 * Live enquiry rates are not used here — sessions expire after ~2h, so review
 * prices the saved sell total. Checkout re-enquires before purchase.
 *
 * `luxPlusTotal` is exclusive of property fees, matching `sellTotal`.
 * `getOverallUserCartTotals` adds `otherFees.propertyFees` onto both.
 */
export function getAccommodationV2CartItemTotals(
  item: App.Checkout.AccommodationV2Item,
  lppMemberPricingEnabled: boolean,
): App.Checkout.ItemViewTotals {
  const propertyFees = item.propertyFees ?? 0
  const extraGuestSurcharge = item.surchargePayableAtProperty ?? 0
  const memberHotelPrice = lppMemberPricingEnabled ? (item.luxPlusTotal ?? 0) : 0

  return {
    price: item.sellTotal,
    memberPrice: memberHotelPrice,
    value: item.sellTotal,
    memberValue: 0,
    surcharge: 0,
    // v2 collects any surcharge we charge inside `sellTotal`, so the whole figure here is the
    // part the property collects. The payment summary reads `otherFees.extraGuestSurcharge`;
    // the per-item breakdown line reads this one.
    extraGuestSurcharge,
    taxesAndFees: 0,
    // Guarded on both: a property can charge an extra guest surcharge and no property fees,
    // and omitting `otherFees` there would hide it from the due-at-property total entirely.
    ...(propertyFees || extraGuestSurcharge
      ? { otherFees: { propertyFees, extraGuestSurcharge } }
      : {}),
  }
}

/**
 * The rate's RRP marked as the offer page marks it, so "Valued up to" and the saving read the same
 * at checkout. The enquiry rate is un-marked, as suppliers price it; the session price beside it is
 * marked only when the enquiry priced a markup onto the rate (`sessionRecord.channelMarkup`).
 */
function markValuedUpToPrices(
  item: App.Checkout.AccommodationV2Item,
  sessionRecord: App.CheckoutState['accommodationSessions'][string] | undefined,
  markupSource: ChannelMarkupSource | undefined,
): { value?: number, memberValue?: number } {
  const rate = sessionRecord?.rate
  if (!rate || !sessionRecord?.channelMarkup) {
    return { value: rate?.totals.valuedUpToPrice, memberValue: rate?.luxPlusTotals?.valuedUpToPrice }
  }
  // The offer id and type the rate was marked with on the offer page (see `getEnquirySessionChannelMarkup`).
  const offerType = item.internalService === 'bedbank' ? OFFER_TYPE_BED_BANK : OFFER_TYPE_ALWAYS_ON
  const markupDetails = resolveChannelMarkupDetails(item.offerId, offerType, markupSource, item.channelMarkupLeadPricing)
  const memberMarkupDetails = resolveMemberChannelMarkupDetails(item.offerId, offerType, markupSource, item.channelMarkupLeadPricing)
  const memberValue = rate.luxPlusTotals?.valuedUpToPrice
  return {
    value: applyChannelMarkupAndRoundUpToTen(rate.totals.valuedUpToPrice, markupDetails),
    memberValue: memberValue != null ? applyChannelMarkupAndRoundUpToTen(memberValue, memberMarkupDetails) : undefined,
  }
}

export function getAccommodationV2ItemView(
  intl: IntlShape,
  item: App.Checkout.AccommodationV2Item,
  sessionRecord: App.CheckoutState['accommodationSessions'][string] | undefined,
  property: App.Property.Property | undefined,
  lppMemberPricingEnabled: boolean,
  markupSource?: ChannelMarkupSource,
): App.WithDataStatus<App.Checkout.AccommodationV2AccommodationItemView> {
  const session = sessionRecord?.data
  const rate = sessionRecord?.rate
  const outcome = sessionRecord?.outcome
  const room = property?.rooms[item.roomId] ?? (session ? property?.rooms[session.data.room.roomId] : undefined)
  const occupancy = session ? occupancyFromSession(session) : item.occupancy
  const soldOut = outcome === 'unavailable' || (!rate && outcome != null)
  const image = room?.images[0] ?? property?.images[0] ?? {}
  const primaryTitle = room?.name || rate?.name || ''
  const propertyFees = session?.data.pricing.propertyFees ?? rate?.totals.propertyFees ?? 0
  // Session first, rate second, matching `propertyFees` and `price`: a checkout enquiry may have
  // rematched onto a different rate, and the session is what svc-order books against.
  const extraGuestSurcharge =
    session?.data.pricing.surchargePayableAtProperty ??
    rate?.totals.surchargePayableAtProperty ??
    0
  // svc-order charges `session.data.pricing.sellTotal`. Property fees are stored
  // separately from `taxesAndFees` and shown as due-at-property via `otherFees`,
  // not added into `price`.
  const price = session?.data.pricing.sellTotal ?? rate?.totals.sellingPrice ?? 0
  const memberPrice = lppMemberPricingEnabled
    ? (session?.data.pricing.luxPlusTotal ?? rate?.luxPlusTotals?.sellingPrice ?? 0)
    : 0
  const valuedUpTo = markValuedUpToPrices(item, sessionRecord, markupSource)
  const value = valuedUpTo.value ?? price

  const targetTotals: App.Checkout.ItemViewTotals = session || rate ? {
    price,
    memberPrice,
    joinerExtraSaving: memberPrice > 0 ? rate?.joinerExtraSaving : undefined,
    value,
    // Members are shown `memberValue` as the offer value, so fall back to the public value when LPP is off.
    memberValue: lppMemberPricingEnabled ? (valuedUpTo.memberValue ?? memberPrice) : value,
    surcharge: 0,
    // Whole amount is property-collected for v2; anything we collect is already in `price`.
    extraGuestSurcharge,
    taxesAndFees: rate?.totals.taxesAndFees ?? 0,
    otherFees: propertyFees || extraGuestSurcharge
      ? { propertyFees, extraGuestSurcharge }
      : undefined,
  } : emptyTotals()

  // An applied loyalty package upgrade books the session's target rate but charges the source
  // price; the difference is paid as a loyalty benefit at purchase (as for legacy bedbank items).
  // The stay is still in the target room, so the property collects the booked rate's surcharge —
  // `extraGuestSurcharge`, off the session svc-order books — not the source rate's.
  const upgradeSourceTotals = isPropertyRateLoyaltyPackageUpgradeItem(item)
    ? getPropertyRateUpgradeSourceItemTotals(item, extraGuestSurcharge)
    : undefined
  // Member pricing follows the same LPP gate as the target's totals.
  const sourceTotals = upgradeSourceTotals && !lppMemberPricingEnabled
    ? { ...upgradeSourceTotals, memberPrice: 0, memberValue: upgradeSourceTotals.value }
    : upgradeSourceTotals
  const appliedPropertyRateUpgrade: App.Checkout.AccommodationItemViewLoyaltyAppliedUpgrade | undefined = sourceTotals ? {
    upgradeType: 'package',
    originTotals: sourceTotals,
    originPrimaryTitle: item.loyaltyPropertyRateUpgrade?.sourceName ?? '',
    targetTotals,
    targetPrimaryTitle: primaryTitle,
    targetImage: image,
    targetValue: targetTotals.value,
    targetInclusions: rate?.inclusions,
  } : undefined

  const view: App.Checkout.AccommodationV2AccommodationItemView = {
    kind: 'accommodationV2',
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    item,
    image,
    primaryTitle,
    occupancy,
    propertyId: item.propertyId,
    rate,
    inclusions: rate?.inclusions,
    bonusInclusions: rate?.bonusInclusions,
    luxPlusInclusions: rate?.luxPlusInclusions,
    bundleInclusions: item.bundleOptin ? undefined : rate?.bundleInclusions,
    optInBundleInclusions: item.bundleOptin
      ? getOptInBundleInclusionsFromRate(rate, item)
      : undefined,
    removeLabel: intl.formatMessage({
      id: 'WmkzQGWSf9s2KSRQdBJ4',
      defaultMessage: 'Remove',
      description: 'Remove button label for accommodationV2 checkout item',
    }),
    soldOut,
    enquiryError: sessionRecord?.error,
    loyalty: {
      productType: getAccommodationV2LoyaltyProductType(rate),
      // Points burn sends this back to svc-order, which accepts the quoted margin within its margin
      // tolerance. Loyalty only: promo keeps the room's own margin.
      mx: sessionRecord?.toleratedLoyaltyMx,
    },
    mx: rate?.totals.mx,
    title: primaryTitle,
    totals: sourceTotals ?? targetTotals,
    ...(appliedPropertyRateUpgrade && { appliedPropertyRateUpgrade }),
  }

  const hasRequiredData = !!session && !!property && !sessionRecord?.error && (outcome === 'unavailable' || !!rate)

  return { hasRequiredData, data: view }
}
